/**
 * AdminCmsPagesPage — List of block-based CMS pages
 *
 * /admin/cms-pages
 *   - Lists all pages with status (draft / published / scheduled)
 *   - Create new (with slug)
 *   - Duplicate / Delete
 *   - Click → opens editor at /admin/cms-pages/:id
 */
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, FileText, Search, Copy, Trash2, ExternalLink, Edit3 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";

const STATUS_LABELS = {
    draft: { label: "Taslak", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
    published: { label: "Yayında", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" },
    scheduled: { label: "Zamanlı", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300" },
};

export default function AdminCmsPagesPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [creating, setCreating] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [newSlug, setNewSlug] = useState("");

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        load();
    }, [user, authLoading, statusFilter]); // eslint-disable-line

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set("status", statusFilter);
            if (q) params.set("q", q);
            const r = await api.get(`/admin/cms/pages?${params.toString()}`);
            setItems(r.data.items || []);
        } finally { setLoading(false); }
    };

    const create = async () => {
        if (!newTitle.trim()) return;
        try {
            const r = await api.post("/admin/cms/pages", { title: newTitle.trim(), slug: newSlug.trim() || undefined });
            navigate(`/admin/cms-pages/${r.data.id}`);
        } catch (e) {
            alert(e.response?.data?.detail || "Sayfa oluşturulamadı");
        }
    };

    const duplicate = async (id) => {
        try {
            await api.post(`/admin/cms/pages/${id}/duplicate`);
            load();
        } catch (e) { alert(e.response?.data?.detail || "Kopyalanamadı"); }
    };

    const remove = async (id, title) => {
        if (!window.confirm(`"${title}" sayfasını silmek istediğinize emin misiniz?`)) return;
        await api.delete(`/admin/cms/pages/${id}`);
        load();
    };

    const filtered = items.filter((p) => !q || p.title.toLowerCase().includes(q.toLowerCase()) || p.slug.toLowerCase().includes(q.toLowerCase()));

    return (
        <AdminLayout
            title="CMS Sayfaları (Blok Tabanlı)"
            action={
                <button data-testid="new-page-btn" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold">
                    <Plus className="h-4 w-4" /> Yeni Sayfa
                </button>
            }
        >
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-5">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <input data-testid="search-pages" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sayfa adı veya slug..." className="w-full pl-9 pr-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm" />
                </div>
                <select data-testid="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm">
                    <option value="">Tüm Durumlar</option>
                    <option value="draft">Taslak</option>
                    <option value="published">Yayında</option>
                    <option value="scheduled">Zamanlı</option>
                </select>
            </div>

            {creating && (
                <div className="mb-5 p-4 rounded-2xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900">
                    <h3 className="font-display text-base font-bold mb-3 text-slate-900 dark:text-white">Yeni Sayfa Oluştur</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input data-testid="new-title" placeholder="Sayfa başlığı (örn. Yaz 2026 Kampanyası)" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="w-full px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm" />
                        <input data-testid="new-slug" placeholder="URL slug (opsiyonel — başlıktan otomatik)" value={newSlug} onChange={(e) => setNewSlug(e.target.value)} className="w-full px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-mono" />
                    </div>
                    <div className="flex gap-2 mt-3">
                        <button onClick={create} data-testid="confirm-create" className="px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">Oluştur ve Düzenle</button>
                        <button onClick={() => { setCreating(false); setNewTitle(""); setNewSlug(""); }} className="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-bold">İptal</button>
                    </div>
                </div>
            )}

            {/* List */}
            {loading ? (
                <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />)}</div>
            ) : filtered.length === 0 ? (
                <div className="p-10 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500 text-sm mb-4">Henüz hiç CMS sayfası yok. İlk sayfanızı oluşturun.</p>
                    <button onClick={() => setCreating(true)} className="px-5 py-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">+ İlk Sayfa</button>
                </div>
            ) : (
                <div className="space-y-2" data-testid="cms-pages-list">
                    {filtered.map((p) => {
                        const st = STATUS_LABELS[p.status] || STATUS_LABELS.draft;
                        return (
                            <div key={p.id} data-testid={`page-row-${p.id}`} className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center gap-4 hover:border-sky-400 transition-colors">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-semibold text-slate-900 dark:text-white truncate">{p.title}</h3>
                                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                                        {p.show_in_header && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300">Menüde</span>}
                                        {p.show_in_footer && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300">Footer</span>}
                                    </div>
                                    <div className="text-xs text-slate-500 font-mono mt-1 truncate">/p/{p.slug}</div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">Son güncelleme: {new Date(p.updated_at).toLocaleString("tr-TR")}</div>
                                </div>
                                <div className="flex items-center gap-1">
                                    {p.status === "published" && (
                                        <Link to={`/p/${p.slug}`} target="_blank" data-testid={`view-${p.id}`} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" title="Yayını görüntüle">
                                            <ExternalLink className="h-4 w-4" />
                                        </Link>
                                    )}
                                    <button onClick={() => navigate(`/admin/cms-pages/${p.id}`)} data-testid={`edit-${p.id}`} className="p-2 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-950/40 text-sky-600" title="Düzenle">
                                        <Edit3 className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => duplicate(p.id)} data-testid={`dup-${p.id}`} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600" title="Kopyala">
                                        <Copy className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => remove(p.id, p.title)} data-testid={`del-${p.id}`} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600" title="Sil">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </AdminLayout>
    );
}
